import { useQuery } from '@apollo/client';
import {
    WorldMap,
    WorldMapVariables,
    WorldMap_worldMap
} from 'graphql/query/__generated__/WorldMap';
import {
    WorldMapMarkerStory,
    WorldMapMarkerStoryVariables
} from 'graphql/query/__generated__/WorldMapMarkerStory';
import { WORLD_MAP_MARKER_STORY_QUERY, WORLD_MAP_QUERY } from 'graphql/query/worldQuery';
import { useViewMapQueryParams } from 'hooks/map/useViewMapQueryParams';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { mapGraphQLMarkers } from 'libs/utils/mapping/worldGrahQLMaps';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { CtxEdit } from 'session/ctxEdit';

export const useGetMap = (
    mapId: string
): {
    map: WorldMap_worldMap;
} => {
    const ctxEdit = useContext(CtxEdit);
    const { getTokenForMapId } = useWorldMapLocalStorage();
    const [mapLoaded, setMapLoaded] = useState(false);
    const navigate = useNavigate();
    const params = useViewMapQueryParams();

    const map = useQuery<WorldMap, WorldMapVariables>(WORLD_MAP_QUERY, {
        variables: {
            worldMapId: mapId,
            token: getTokenForMapId(mapId)
        },
        fetchPolicy: 'network-only',
        onError: (error) => {
            if (error && error.message.includes('Not Found')) {
                navigate('/admin');
            }

            if (error && error.message.includes('Unauthorized')) {
                navigate('/map/' + mapId);
            }
        }
    });

    const marker = useQuery<WorldMapMarkerStory, WorldMapMarkerStoryVariables>(
        WORLD_MAP_MARKER_STORY_QUERY,
        {
            variables: {
                worldMapId: mapId,
                token: getTokenForMapId(mapId),
                language: params.language
            },
            skip: !mapId
        }
    );

    // A bit of a hack - when navigating out of editor, refetch data
    // so when you come back to the editor there are fresh data loaded
    // plus also, it works for DEV environment reload nicely
    useEffect(() => {
        return () => {
            map.refetch();
            marker.refetch();
        };
    }, []);

    useEffect(() => {
        if (map.data?.worldMap?.worldMapId && marker.data?.worldMapView?.markers && !mapLoaded) {
            ctxEdit.setWorldMarkers(mapGraphQLMarkers(marker.data?.worldMapView?.markers ?? []));
            ctxEdit.setMap(map.data?.worldMap);
            const visibleSetIds =
                map.data?.worldMap?.worldMapSets
                    ?.filter((e) => e.isVisibleOnLoad)
                    ?.map((e) => e.worldMapSetId) ?? [];
            ctxEdit.setVisibleMapSetIds(visibleSetIds);
            setMapLoaded(true);
        }
    }, [map.data?.worldMap?.worldMapId, marker.data?.worldMapView?.markers]);

    return {
        map: map.data?.worldMap
    };
};
